import { useState } from 'react';
import type { ReactElement } from 'react';

import { Loader2, RefreshCw } from 'lucide-react';
import { useCollapsibleSection } from 'shared/hooks/collapsibleSections';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { useUpdateMinPricesMutation, useUpdateStockAndPricesMutation } from '../api/master.api';
import type { StockUpdateStepResult } from '../api/types';

interface UpdateStockSectionProps {
  projectId: number;
  projectName?: string;
  /** False when the backend detected no stock integration for this project — the stock action is disabled. */
  stockSupported: boolean;
  /** Detected integration type, shown as a badge when supported. */
  integrationType?: string | null;
}

type RunKind = 'stock' | 'minPrices';

interface StepView {
  label: string;
  success: boolean;
  /** Failure reason. Successful steps carry no detail — the strategy is internal noise for operators. */
  detail?: string;
}

interface RunResult {
  success: boolean;
  summary: string;
  steps: StepView[];
}

export const UNSUPPORTED_INTEGRATION_MESSAGE = 'Esta integración aún no está disponible';

/**
 * These routes report their 4xx through Koa's `ctx.throw(status, 'message')`, which answers with a
 * text/plain body — so RTK Query hands us a string `data` that the shared helper ignores by design.
 * Scoped here on purpose: it only changes what this section shows, not every other caller.
 */
const extractRunErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null) {
    const { data, status } = error as { data?: unknown; status?: unknown };
    if (status !== 'PARSING_ERROR' && typeof data === 'string' && data.trim()) return data;
  }

  return extractErrorMessage(error, fallback);
};

const toStepView = (label: string, step: StockUpdateStepResult): StepView => ({
  label,
  success: step.success,
  detail: step.success ? undefined : step.error ?? step.strategy,
});

const UpdateStockSection = ({
  projectId,
  projectName,
  stockSupported,
  integrationType,
}: UpdateStockSectionProps): ReactElement => {
  const sectionProps = useCollapsibleSection('stock-and-min-prices');
  const [pendingRun, setPendingRun] = useState<RunKind | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);

  const [updateStockAndPrices, { isLoading: isUpdatingStock }] = useUpdateStockAndPricesMutation();
  const [updateMinPrices, { isLoading: isUpdatingMinPrices }] = useUpdateMinPricesMutation();

  const isLoading = isUpdatingStock || isUpdatingMinPrices;
  const label = projectName ?? `el proyecto ${projectId}`;

  const runStock = async (): Promise<void> => {
    // forceUpdate stays false for manual runs: schedulers use true on their cadence; operators should not bypass dedup guards.
    const response = await updateStockAndPrices({ id: projectId, forceUpdate: false }).unwrap();
    setResult({
      success: response.stockUpdate.success && response.minPricesUpdate.success,
      summary: `Integración: ${response.integrationType}.`,
      steps: [toStepView('Stock', response.stockUpdate), toStepView('Min prices', response.minPricesUpdate)],
    });
  };

  const runMinPrices = async (): Promise<void> => {
    const response = await updateMinPrices(projectId).unwrap();
    setResult({
      success: response.minPricesUpdate.success,
      summary: 'Actualización de min prices.',
      steps: [toStepView('Min prices', response.minPricesUpdate)],
    });
  };

  const handleConfirm = async (kind: RunKind): Promise<void> => {
    setPendingRun(null);

    try {
      if (kind === 'stock') await runStock();
      else await runMinPrices();
    } catch (error) {
      setResult({
        success: false,
        summary: extractRunErrorMessage(
          error,
          kind === 'stock' ? 'No se pudo actualizar el stock.' : 'No se pudo actualizar los min prices.'
        ),
        steps: [],
      });
    }
  };

  const confirmCopy =
    pendingRun === 'stock'
      ? {
          title: `¿Actualizar stock de ${label}?`,
          description:
            'Esta acción consultará la integración configurada, actualizará las propiedades disponibles y luego calculará los precios mínimos. Puede tardar varios minutos.',
        }
      : {
          title: `¿Actualizar min prices de ${label}?`,
          description:
            'Esta acción recalculará los precios mínimos a partir de las propiedades ya cargadas en Lidz, sin consultar el CRM. Puede tardar varios minutos.',
        };

  return (
    <>
      <Accordion
        type="single"
        collapsible
        {...sectionProps}
        className="bg-bg-surface border border-border-subtle rounded-xl"
      >
        <AccordionItem value="content" className="border-none">
          <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-bg-surface-raised rounded-xl data-[state=open]:rounded-b-none">
            <span className="flex items-center gap-3">
              <span className="font-semibold text-text-default">Stock y min prices</span>
              <Badge className={stockSupported ? 'bg-success-light text-success' : 'bg-neutral-100 text-text-subtle'}>
                {stockSupported ? integrationType ?? 'Integración detectada' : 'Sin integración'}
              </Badge>
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 border-t border-border-subtle">
            <div className="flex flex-col gap-4 pt-4">
              <p className="text-sm text-text-muted">
                Sincroniza el stock desde la integración CRM de {label} y recalcula los precios mínimos si corresponde.
              </p>

              <div className="flex flex-col items-start gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isLoading || !stockSupported}
                  onClick={() => setPendingRun('stock')}
                  className="gap-2"
                >
                  {isUpdatingStock ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Actualizar stock y min prices
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                  onClick={() => setPendingRun('minPrices')}
                  className="gap-2"
                >
                  {isUpdatingMinPrices ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Actualizar min prices
                </Button>
              </div>

              {!stockSupported && <p className="text-sm text-text-muted">{UNSUPPORTED_INTEGRATION_MESSAGE}</p>}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <AlertDialog open={pendingRun !== null} onOpenChange={(open) => !open && setPendingRun(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmCopy.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoading}
              onClick={() => {
                if (pendingRun) void handleConfirm(pendingRun);
              }}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={result !== null} onOpenChange={(open) => !open && setResult(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {result?.success ? 'Actualización completada' : 'Actualización con errores'}
            </AlertDialogTitle>
            <AlertDialogDescription className="break-words">{result?.summary}</AlertDialogDescription>
          </AlertDialogHeader>

          {result && result.steps.length > 0 && (
            <ul className="flex flex-col gap-2">
              {result.steps.map((step) => (
                <li key={step.label} className="rounded-lg border border-border-subtle px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-text-default">{step.label}</span>
                    <Badge className={step.success ? 'bg-success-light text-success' : 'bg-error-light text-error'}>
                      {step.success ? 'OK' : 'Error'}
                    </Badge>
                  </div>
                  {step.detail && <p className="mt-1 text-xs text-text-muted break-words">{step.detail}</p>}
                </li>
              ))}
            </ul>
          )}

          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setResult(null)}>Cerrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UpdateStockSection;
